import { cn } from '@poseidon/components/cn';
import { motion } from 'framer-motion';
import React from 'react';
import toast, { CheckmarkIcon, ErrorIcon, type Toast } from 'react-hot-toast';

import { Button } from '@poseidon/components/Input2';
import { Arrows, General } from '@poseidon/components/StrokeIcons';

type Props = {
  title: string;
  message: string;
  ctaText?: string;
  onCtaClick?: () => void;
  t: Toast;
  type?: 'success' | 'error' | 'info';
};

const CustomToast = ({ title, message, t, ctaText, onCtaClick, type = 'info' }: Props) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const onClose = () => {
    toast.dismiss(t.id);
    setIsOpen(false);
  };

  React.useEffect(() => {
    setIsOpen(true);

    return () => {
      setIsOpen(false);
    };
  }, []);

  const showCta = ctaText !== undefined && ctaText !== '';

  return (
    <motion.div
      initial={false}
      animate={isOpen ? 'open' : 'closed'}
      variants={{
        open: {
          opacity: 1,
          y: 0,
          transition: {
            duration: 0.3,
            ease: 'easeOut',
          },
        },
        closed: {
          opacity: 0,
          y: 20,
          transition: {
            duration: 0.4,
            ease: 'easeIn',
          },
        },
      }}
      style={{ pointerEvents: isOpen ? 'auto' : 'none' }}>
      <div
        className={cn(
          'relative flex w-[360px] flex-col items-start overflow-hidden rounded-xl px-5 py-4 text-left text-white shadow-[0_4px_12px_rgba(0,0,0,0.15)]',
          type === 'info' && 'bg-linear-to-br from-[#1e40af] to-[#1e3a8a]',
          type === 'success' && 'bg-linear-to-br from-[#059669] to-[#047857]',
          type === 'error' && 'bg-linear-to-br from-[#dc2626] to-[#b91c1c]',
          !showCta && 'pb-4',
        )}>
        <span
          className="absolute right-3 top-3 cursor-pointer opacity-70 transition-opacity hover:opacity-100"
          onClick={onClose}>
          <General.XMark />
        </span>
        <h2 className="m-0 mb-1.5 flex items-center gap-2 text-[15px] font-semibold">
          {title}
          {type === 'success' && <CheckmarkIcon />}
          {type === 'error' && <ErrorIcon />}
        </h2>
        <p className="m-0 text-sm font-normal leading-normal opacity-95">{message}</p>
        {showCta && (
          <Button
            className="mt-3"
            rIcon={<Arrows.ArrowRight />}
            onClick={() => {
              onClose();
              onCtaClick?.();
            }}>
            {ctaText}
          </Button>
        )}
      </div>
    </motion.div>
  );
};

export default CustomToast;
